import { useEffect, useLayoutEffect, useRef } from "react";
import type { ReactElement, ReactNode, RefObject } from "react";
import { createPortal } from "react-dom";

import { pushEscapeLayer } from "../escapeLayers";
import styles from "./Popover.module.css";

export type FloatingAlign = "start" | "end" | "center";
export type FloatingSide = "below" | "above";

export interface AnchorBox {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

/** The mockup's gap between an anchor and its floating layer, and the gutter kept to the viewport. */
const GAP = 4;
const GUTTER = 8;

/**
 * Where a floating layer goes: on the preferred side of its anchor, 4px away, aligned to the anchor's
 * start, end or centre, flipped to the other side when it does not fit, and always 8px inside the
 * viewport (the mockup's `openPop`).
 */
export function placeFloating(
  anchor: AnchorBox,
  size: { readonly width: number; readonly height: number },
  viewport: { readonly width: number; readonly height: number },
  align: FloatingAlign,
  side: FloatingSide = "below",
): { left: number; top: number } {
  const wanted =
    align === "end"
      ? anchor.right - size.width
      : align === "center"
        ? (anchor.left + anchor.right - size.width) / 2
        : anchor.left;
  const left = Math.max(GUTTER, Math.min(wanted, viewport.width - size.width - GUTTER));

  const below = anchor.bottom + GAP;
  const above = anchor.top - size.height - GAP;
  const fitsBelow = below + size.height <= viewport.height - GUTTER;
  const fitsAbove = above >= GUTTER;
  let top: number;
  if (side === "above") top = fitsAbove || !fitsBelow ? Math.max(GUTTER, above) : below;
  else top = fitsBelow || !fitsAbove ? below : Math.max(GUTTER, above);
  return { left, top };
}

/** Write a placement as custom properties: the CSP forbids `style=""`, not the CSSOM. */
export function applyPlacement(element: HTMLElement, placement: { left: number; top: number }): void {
  element.style.setProperty("--floating-left", `${String(placement.left)}px`);
  element.style.setProperty("--floating-top", `${String(placement.top)}px`);
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** How many floating layers are open; the body keeps `popover-open` while any is. */
let openLayers = 0;

/** Marks the body while a layer is open, so `base.css` can turn off pointer events on frames. */
export function useFrameGuard(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;
    openLayers += 1;
    document.body.classList.add("popover-open");
    return () => {
      openLayers -= 1;
      if (openLayers === 0) document.body.classList.remove("popover-open");
    };
  }, [active]);
}

export interface PopoverProps {
  readonly open: boolean;
  /** Called on Escape and on a press outside both the popover and its anchor. */
  readonly onClose: () => void;
  readonly anchorRef: RefObject<HTMLElement | null>;
  readonly align?: FloatingAlign | undefined;
  /** The accessible name of the dialog (from `t()`); or `labelledBy` for a visible heading. */
  readonly label?: string | undefined;
  readonly labelledBy?: string | undefined;
  /**
   * `dialog` (default) for a panel of content; `none` when the children carry their own role,
   * as `Menu` does with `role="menu"`.
   */
  readonly role?: "dialog" | "none" | undefined;
  /** A width step (the mockup's share 360, versions 420, model picker 520 px). */
  readonly width?: "auto" | "sm" | "md" | "lg" | undefined;
  readonly id?: string | undefined;
  readonly children: ReactNode;
}

/**
 * A floating panel anchored to a control (the mockup's `.pop`).
 *
 * It renders into `document.body`, so no scrolling or clipping ancestor can cut it, and positions
 * itself with `placeFloating`, again on resize and scroll. Opening moves the focus to the element
 * marked `data-autofocus`, else to the first control. Escape closes it and gives the focus back to
 * the anchor; a press outside closes it and leaves the focus where the press put it; a press on the
 * anchor is left to the anchor, which toggles, and a press inside a modal layer stacked over it (a
 * confirmation it opened) leaves it open. While it is open the body carries `popover-open`, the
 * iframe-click guard: a press inside the cross-origin preview frame never reaches this document.
 */
export function Popover({
  open,
  onClose,
  anchorRef,
  align = "start",
  label,
  labelledBy,
  role = "dialog",
  width = "auto",
  id,
  children,
}: PopoverProps): ReactElement | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useFrameGuard(open);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = (): void => {
      const panel = panelRef.current;
      const anchor = anchorRef.current;
      if (panel === null || anchor === null) return;
      applyPlacement(
        panel,
        placeFloating(
          anchor.getBoundingClientRect(),
          { width: panel.offsetWidth, height: panel.offsetHeight },
          { width: window.innerWidth, height: window.innerHeight },
          align,
        ),
      );
    };
    place();
    const panel = panelRef.current;
    (panel?.querySelector<HTMLElement>("[data-autofocus]") ?? panel?.querySelector<HTMLElement>(FOCUSABLE))?.focus();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, align, anchorRef]);

  useEffect(() => {
    if (!open) return undefined;
    // Escape closes this popover only while it is the topmost layer (`escapeLayers`, verifier m1).
    const removeLayer = pushEscapeLayer(() => {
      closeRef.current();
      anchorRef.current?.focus();
    });
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node | null;
      if (target === null) return;
      if (panelRef.current?.contains(target) === true) return;
      if (anchorRef.current?.contains(target) === true) return;
      // A modal layer stacked over this popover (a confirmation it opened) is not "outside".
      if (target instanceof Element && target.closest('[aria-modal="true"]') !== null) return;
      closeRef.current();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      removeLayer();
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [open, anchorRef]);

  if (!open) return null;

  const className = [styles.popover, styles[width]].join(" ");
  const panel =
    role === "dialog" ? (
      <div ref={panelRef} id={id} className={className} role="dialog" aria-label={label} aria-labelledby={labelledBy}>
        {children}
      </div>
    ) : (
      <div ref={panelRef} id={id} className={className}>
        {children}
      </div>
    );
  return createPortal(panel, document.body);
}
